import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DataResponse } from '../../../core/models/data-response';
import { ConfigService } from '../../../core/services/config/config.service';
import { UtilityService } from '../../../core/services/utility/utility.service';
import { Appointment, AppointmentNoteUrl, AppointmentSearchRequest } from '../../models/appointment';

@Injectable({
  providedIn: 'root',
})
export class AppointmentsService {
  private utilityService = inject(UtilityService);
  private configService = inject(ConfigService);
  private config = this.configService.config;

  appointmentsEndpoint = `${this.config.scheduleApiUrl}/v1/PatientPortal/Appointments`;
  appointmentsSearchEndpoint = this.appointmentsEndpoint + '/Search';
  appointmentNotesEndpoint = `${this.config.scheduleApiUrl}/v1/PatientPortal/AppointmentNotes`;

  getNextPatientAppointment(patientId: number): Observable<Appointment> {
    const url = `${this.appointmentsEndpoint}?patientId=${patientId}`;
    return this.utilityService.getRequest<Appointment>(url);
  }

  getUpcomingPatientAppointments(
    patientId: number,
    appointmentSearchRequest: AppointmentSearchRequest
  ): Observable<Appointment[]> {
    return this.utilityService.postRequest<Appointment[]>(
      `${this.appointmentsSearchEndpoint}?patientId=${patientId}`,
      appointmentSearchRequest,
      { applyDefaultTimeout: true }
    );
  }

  getPastPatientAppointments(
    patientId: number,
    appointmentSearchRequest: AppointmentSearchRequest
  ): Observable<DataResponse<Appointment[]>> {
    return this.utilityService.postRequest<DataResponse<Appointment[]>>(
      `${this.appointmentsSearchEndpoint}?patientId=${patientId}`,
      appointmentSearchRequest,
      { rawResponse: true, applyDefaultTimeout: true }
    );
  }

  /**
   * Exchanges a pat.PatientNote id for a short-lived SAS URL.
   *
   * Every successful call writes a disclosure-audit row on the API side, so it must only be made when the
   * patient actually asks to open the document. Do not call it on render, hover, or scroll — prefetching
   * would re-create the per-row over-disclosure this endpoint exists to remove.
   *
   * Failures are handled by the caller, so this opts out of the global error handler: a note that cannot
   * be opened is one row's problem, and letting ErrorInterceptor dispatch setGlobalError would route the
   * patient to /error, off the appointments page they are still reading.
   *
   * That opt-out is partial by design, and the design is now explicit rather than incidental. A 403
   * still navigates to /unauthorized — and here that is the intended outcome, not a gap. Every
   * resource-level failure on this endpoint returns the same RecordNotFound shape so a note id cannot
   * be probed, which leaves the PatientAccess policy as the only source of a 403: revoked proxy access
   * or session drift. That is a denial of the patient context rather than of one document, so routing
   * off the page is the correct report. Local handling covers what is genuinely one row's problem —
   * 404 RecordNotFound, 5xx, and network failure — each surfaced on the row via failedNoteId.
   *
   * What makes that hold is that this read uses `skipGlobalError` and NOT `SKIP_UNAUTHORIZED_REDIRECT`.
   * The redirect is gated on the latter, which only blob/SAS legs set (`downloadRead()` /
   * `htmlPreviewRead()`), where a 403 means an expired signature rather than a denial. Do not collapse
   * the two tokens: that would silently reverse this endpoint's intended behaviour. See ADR-014.
   */
  getAppointmentNoteUrl(patientId: number, patientNoteId: number): Observable<AppointmentNoteUrl> {
    const url = `${this.appointmentNotesEndpoint}/${patientNoteId}?patientId=${patientId}`;
    return this.utilityService.getRequest<AppointmentNoteUrl>(url, { skipGlobalError: true });
  }
}
